import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';

export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
};

export const configured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);
const useEmulators = import.meta.env.VITE_USE_EMULATORS === '1';

export const app = initializeApp(configured ? firebaseConfig : { apiKey: 'demo', projectId: 'demo-duoplate', appId: 'demo' });
export const auth = getAuth(app);

// DuoPlate's own named database inside the project. Offline cache keeps the app usable on the metro.
export const db = initializeFirestore(
  app,
  { localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }) },
  'duoplate',
);

export const functions = getFunctions(app, 'europe-west3');

if (useEmulators) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

/** DuoPlate accounts have uids starting with duo_ (created by the signUp function). */
export const isDuoUid = (uid: string) => /^duo_[A-Za-z0-9]{24}$/.test(uid);

export const api = {
  signUp: httpsCallable<{ email: string; password: string; displayName: string }, { ok: boolean }>(functions, 'signUp'),
  createDuo: httpsCallable<{ name: string }, { duoId: string }>(functions, 'createDuo'),
  createInvite: httpsCallable<void, { code: string; expiresInDays: number }>(functions, 'createInvite'),
  joinDuo: httpsCallable<{ code: string }, { duoId: string }>(functions, 'joinDuo'),
  leaveDuo: httpsCallable<void, { ok: boolean }>(functions, 'leaveDuo'),
  deleteMyAccount: httpsCallable<void, { ok: boolean }>(functions, 'deleteMyAccount'),
  coachChat: httpsCallable<{ text: string; context?: { phase?: { label: string; day: number } } }, { threadId: string; reply: string; changes: { id: string; label: string }[]; places?: unknown[]; quota?: Quota }>(functions, 'coachChat', { timeout: 300_000 }),
  coachUndo: httpsCallable<{ changeId: string }, { ok: boolean }>(functions, 'coachUndo'),
  coachNewThread: httpsCallable<void, { threadId: string }>(functions, 'coachNewThread'),
  coachConsent: httpsCallable<void, { ok: boolean }>(functions, 'coachConsent'),
  cheerPartner: httpsCallable<void, { ok: boolean }>(functions, 'cheerPartner'),
  adminListUsers: httpsCallable<void, { users: AdminUser[] }>(functions, 'adminListUsers'),
  adminSetAi: httpsCallable<{ uid: string; enabled: boolean }, { ok: boolean }>(functions, 'adminSetAi'),
};

/** Coach messages this month for the duo; limit null = unlimited (owner-granted access). */
export interface Quota { used: number; limit: number | null; tier: 'unlimited' | 'plus' | 'free' }

export interface AdminUser {
  uid: string; name: string; email: string; createdAt: string | null; ai: boolean; admin: boolean;
  consentAi: boolean; inDuo: boolean; monthMessages: number; monthTokens: number;
}
